import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatBJ } from '../utils/datetime';

// ─── 站内通知收件箱（弹层）─────────────────────────────────────────────────────
// 同一天收到很多条通知时，不再一条条弹、一条条关，而是一个弹层里翻页看：
//   · 打开时停在最新一条；“上一条 / 下一条”（或左右滑动、键盘 ← →）在时间顺序里切换，右上角显示“第 N / 共 M 条”
//   · “关闭本条”：只关掉当前这条，自动显示下一条；“全部关闭”（一键）：所有通知一次标记已读并关闭弹层
//   · 点右上角 ×、点遮罩、按 Esc：等同“全部关闭”（已经给你看过了，不会再逐条追着弹）
//   · 有新通知进来时不会打断正在看的这条，只是总数 +1，右下角“下一条”会亮出“新”
// 只负责显示与交互，数据和已读状态由父组件管理（items 按时间从早到晚，index 是当前位置）。
export default function PushInbox({ items, index, onIndex, onCloseOne, onCloseAll, newCount = 0 }) {
  const total = items.length;
  const cur = items[Math.min(index, total - 1)];
  const touch = useRef(null);
  const bodyRef = useRef(null);

  // 键盘：← → 翻页，Esc 全部关闭
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') onIndex(Math.max(0, index - 1));
      else if (e.key === 'ArrowRight') onIndex(Math.min(total - 1, index + 1));
      else if (e.key === 'Escape') onCloseAll();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, onIndex, onCloseAll]);

  // 换条后正文滚回顶部
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [cur && cur.id]);

  if (!cur) return null;
  const canPrev = index > 0;
  const canNext = index < total - 1;
  const multi = total > 1;

  // 左右滑动翻页（横向位移明显大于纵向，避免和正文上下滚动打架）
  const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    const s = touch.current; touch.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx < 0 && canNext) onIndex(index + 1);       // 向左滑 → 下一条
    else if (dx > 0 && canPrev) onIndex(index - 1);  // 向右滑 → 上一条
  };

  const navBtn = (enabled) => ({
    flex: 1, height: 40, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: enabled ? 'pointer' : 'default',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, position: 'relative',
    border: '1px solid var(--c-border)', background: 'var(--c-bg)',
    color: enabled ? 'var(--c-text)' : 'var(--c-text-tertiary)', opacity: enabled ? 1 : 0.5,
  });

  return createPortal(
    <div
      className="push-inbox"
      role="dialog" aria-modal="true" aria-label="系统通知"
      onClick={onCloseAll}
      style={{ position: 'fixed', inset: 0, zIndex: 2147483600, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        style={{ background: 'var(--c-surface)', borderRadius: 20, width: '100%', maxWidth: 400, overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', maxHeight: '84vh' }}
      >
        {/* 头部：标题 + 计数 + 关闭 */}
        <div style={{ padding: '18px 18px 8px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Bell size={18} color="var(--c-primary)" style={{ marginTop: 3, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="push-inbox-title" style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.4, overflowWrap: 'anywhere' }}>{cur.title || '系统通知'}</div>
            <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 2 }}>{formatBJ(cur.created_at)}</div>
          </div>
          {multi && (
            <span className="push-inbox-count" style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-secondary)', background: 'var(--c-bg)', borderRadius: 10, padding: '2px 10px', flexShrink: 0, marginTop: 2 }}>
              {index + 1} / {total}
            </span>
          )}
          <button onClick={onCloseAll} aria-label="全部关闭" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
            <X size={18} color="var(--c-text-secondary)" />
          </button>
        </div>

        {/* 正文：很长时框内滚动，按钮始终在下面看得见 */}
        <div ref={bodyRef} className="push-inbox-body" style={{ padding: '4px 18px 16px', fontSize: 14, lineHeight: 1.8, color: 'var(--c-text)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', overflowY: 'auto', minHeight: 60 }}>
          {cur.content}
        </div>

        {/* 翻页 */}
        {multi && (
          <div style={{ padding: '0 18px 10px', display: 'flex', gap: 10 }}>
            <button className="push-inbox-prev" disabled={!canPrev} onClick={() => onIndex(index - 1)} style={navBtn(canPrev)}>
              <ChevronLeft size={16} /> 上一条
            </button>
            <button className="push-inbox-next" disabled={!canNext} onClick={() => onIndex(index + 1)} style={navBtn(canNext)}>
              下一条 <ChevronRight size={16} />
              {newCount > 0 && canNext && (
                <span style={{ position: 'absolute', top: -6, right: -4, background: 'var(--c-danger)', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '1px 6px' }}>新</span>
              )}
            </button>
          </div>
        )}

        {/* 关闭 */}
        <div style={{ padding: '0 18px 18px', display: 'flex', gap: 10 }}>
          {multi && (
            <button className="btn btn-outline push-inbox-close-one" onClick={onCloseOne} style={{ flex: 1, height: 46 }}>关闭本条</button>
          )}
          <button className="btn btn-primary push-inbox-close-all" onClick={onCloseAll} style={{ flex: multi ? 1.3 : 1, height: 46 }}>
            {multi ? `全部关闭（${total} 条）` : '知道了'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
